import { 
  logEvent, 
  setUserProperties, 
  setUserId, 
  getAnalytics 
} from "firebase/analytics";
import { 
  doc, 
  updateDoc, 
  increment, 
  arrayUnion, 
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  setDoc
} from "firebase/firestore";
import { analytics, db } from "../FirebaseIndex";
import { handleFirebaseError } from "../FirebaseUtils/FirebaseErrors";
import { retryHandler } from "../FirebaseUtils/FirebaseRetryHandler";
import debug from "../../../utils/debug"; // Import debug utility

// Helper to validate analytics availability
const validateAnalytics = () => {
  if (!analytics) {
    debug.log("AnalyticsService", "Analytics not available");
    return false;
  }
  return true;
};

// Log custom event to Firebase Analytics
export const logCustomEvent = async (eventName, eventParams = {}) => {
  try {
    debug.log("AnalyticsService", `Logging custom event: ${eventName}`, eventParams);
    if (!validateAnalytics()) {
      return { 
        success: false, 
        reason: "Analytics not available",
        event: eventName 
      };
    }
    
    await retryHandler.execute(async () => {
      logEvent(analytics, eventName, {
        ...eventParams,
        timestamp: new Date().toISOString()
      });
    });
    
    return { 
      success: true, 
      event: eventName 
    };
  } catch (error) {
    debug.error("AnalyticsService", `Error logging analytics event: ${eventName}`, error);
    return handleFirebaseError(error, `Error logging analytics event: ${eventName}`);
  }
};

// Set user properties for analytics
export const setUserAnalyticsProperties = async (properties, userUID = null) => {
  try {
    debug.log("AnalyticsService", "Setting user analytics properties", { properties, userUID });
    if (!validateAnalytics()) {
      return { 
        success: false, 
        reason: "Analytics not available" 
      };
    }
    
    // Set user ID if provided
    if (userUID) {
      await retryHandler.execute(async () => {
        setUserId(analytics, userUID);
      });
    }
    
    await retryHandler.execute(async () => {
      setUserProperties(analytics, properties);
    });
    
    return { 
      success: true, 
      propertiesSet: Object.keys(properties) 
    };
  } catch (error) {
    debug.error("AnalyticsService", "Error setting user analytics properties", error);
    return handleFirebaseError(error, "Error setting user analytics properties");
  }
};

// Track screen view
export const trackScreenView = async (screenName, additionalParams = {}) => {
  try {
    debug.log("AnalyticsService", `Tracking screen view: ${screenName}`, additionalParams);
    if (!validateAnalytics()) {
      return { 
        success: false, 
        reason: "Analytics not available" 
      };
    }
    
    await retryHandler.execute(async () => {
      logEvent(analytics, "screen_view", {
        screen_name: screenName,
        ...additionalParams
      });
    });
    
    return { 
      success: true, 
      screen: screenName 
    };
  } catch (error) {
    debug.error("AnalyticsService", `Error tracking screen view: ${screenName}`, error);
    return handleFirebaseError(error, `Error tracking screen view: ${screenName}`);
  }
};

// Registration event constants
export const REGISTRATION_EVENTS = {
  BASICS_COMPLETE: 'registration_basics_complete',
  SHOWCASE_COMPLETE: 'registration_showcase_complete',
  PREFERENCES_COMPLETE: 'registration_preferences_complete',
  LEGAL_COMPLETE: 'registration_legal_complete',
  REGISTRATION_COMPLETE: 'registration_all_complete'
};

// Log registration progress events
export const logRegistrationProgress = async (step, userData) => {
  debug.log("AnalyticsService", `Logging registration progress: ${step}`, userData);
  return await logCustomEvent(REGISTRATION_EVENTS[step], {
    user_id: userData.id,
    time_taken: userData.timeTaken || 0,
    completion_percentage: userData.completionPercentage || 0,
    user_type: userData.userType || 'unknown'
  });
};

// Log visit to any page - integrated from bookingAnalyticsService
export const logVisit = async (pageName, userUID = null) => {
  try {
    debug.log("AnalyticsService", `Logging visit to page: ${pageName}`, { userUID });
    // Always log the visit to Firebase Analytics if available
    if (validateAnalytics()) {
      await logCustomEvent('page_visit', {
        page_name: pageName,
        user_id: userUID || 'anonymous',
        timestamp: new Date().toISOString()
      });
    }

    // If we have a userUID, also update the Firestore document
    if (userUID) {
      // Determine the correct collection based on the user type (will be fetched or inferred)
      let userType = 'unknown';
      let analyticsCollection = null;
      
      try {
        // Try to infer user type from the page name
        if (pageName.toLowerCase().includes('talent')) {
          userType = 'Talent';
          analyticsCollection = 'TalentAnalytics';
        } else if (pageName.toLowerCase().includes('organiser')) {
          userType = 'Organiser';
          analyticsCollection = 'OrganiserAnalytics';
        } else {
          // Try to query the Users collection to determine user type
          const userRef = doc(db, "Users", userUID);
          const userDoc = await retryHandler.execute(async () => {
            return await getDoc(userRef);
          });
          
          if (userDoc.exists()) {
            userType = userDoc.data()?.profile?.userType || 'unknown';
            analyticsCollection = userType === 'Talent' ? 'TalentAnalytics' : 'OrganiserAnalytics';
          }
        }
        
        if (analyticsCollection) {
          debug.firebase("AnalyticsService", `Updating ${analyticsCollection} for page visit`, { userUID, pageName });
          const analyticsRef = doc(db, analyticsCollection, userUID);
          
          // Update the appropriate analytics document
          await updateDoc(analyticsRef, {
            [`pageVisits.${pageName}`]: increment(1),
            "updatedAt": serverTimestamp()
          });
        }
      } catch (error) {
        debug.error("AnalyticsService", `Error updating page visit analytics for user: ${userUID}`, error);
        // Don't throw, just log the warning as this is non-critical functionality
      }
    }
    
    return { success: true, page: pageName };
  } catch (error) {
    debug.error("AnalyticsService", `Error logging visit to ${pageName}`, error);
    return handleFirebaseError(error, `Error logging visit to ${pageName}`);
  }
};

// Comprehensive Analytics Service
export const AnalyticsService = {
  trackScreenView: async (screenName, additionalParams = {}) => {
    return await trackScreenView(screenName, additionalParams);
  },

  logCustomEvent: async (eventName, eventParams = {}) => {
    return await logCustomEvent(eventName, eventParams);
  },
  
  // Helper function to ensure analytics document exists
  ensureAnalyticsDocumentExists: async (userUID, userType) => {
    try {
      const collectionName = userType === 'Talent' ? 'TalentAnalytics' : 'OrganiserAnalytics';
      const ref = doc(db, collectionName, userUID);
      const docSnap = await getDoc(ref);
      
      if (!docSnap.exists()) {
        debug.log("AnalyticsService", `Creating ${collectionName} document for ${userUID}`);
        
        const initialData = {
          userUID,
          userType,
          createdAt: serverTimestamp(),
          profileViews: {
            totalViews: 0,
            monthlyViews: [],
            uniqueVisitors: 0
          },
          inquiryMetrics: {
            totalInquiries: 0,
            monthlyInquiries: []
          },
          bookingMetrics: {
            totalBookedGigs: 0,
            monthlyBookings: [],
            repeatsPerOrganiser: 0
          },
          repeatClients: [],
          lastUpdated: serverTimestamp()
        };
        
        await setDoc(ref, initialData);
        debug.log("AnalyticsService", `Successfully created ${collectionName} document for ${userUID}`);
      }
      
      return { success: true };
    } catch (error) {
      debug.error("AnalyticsService", "Error ensuring analytics document exists", error);
      return handleFirebaseError(error, "Error ensuring analytics document exists");
    }
  },
  
  // Talent-specific analytics
  talent: {
    logProfileView: async (talentUID, viewerUID) => {
      try {
        debug.log("TalentAnalytics", "Logging profile view", { talentUID, viewerUID });
        
        // Ensure document exists before updating
        await AnalyticsService.ensureAnalyticsDocumentExists(talentUID, 'Talent');
        
        const ref = doc(db, "TalentAnalytics", talentUID);
        await updateDoc(ref, {
          "profileViews.totalViews": increment(1),
          "profileViews.monthlyViews": arrayUnion({
            date: serverTimestamp(),
            viewerUID: viewerUID,
          }),
          "profileViews.uniqueVisitors": increment(1),
        });
        
        // Log analytics event
        await logCustomEvent('talent_profile_view', {
          talent_uid: talentUID,
          viewer_uid: viewerUID
        });
        
        return { success: true };
      } catch (error) {
        debug.error("TalentAnalytics", "Error logging talent profile view", error);
        return handleFirebaseError(error, "Error logging talent profile view");
      }
    },

    logBookingInquiry: async (talentUID, inquiryDetails) => {
      try {
        debug.log("TalentAnalytics", "Logging booking inquiry", { talentUID, ...inquiryDetails });
        
        // Ensure document exists before updating
        await AnalyticsService.ensureAnalyticsDocumentExists(talentUID, 'Talent');
        
        const ref = doc(db, "TalentAnalytics", talentUID);
        await updateDoc(ref, {
          "inquiryMetrics.totalInquiries": increment(1),
          "inquiryMetrics.monthlyInquiries": arrayUnion({
            date: serverTimestamp(),
            ...inquiryDetails,
          }),
        });
        
        // Log analytics event
        await logCustomEvent('talent_booking_inquiry', {
          talent_uid: talentUID,
          ...inquiryDetails
        });
        
        return { success: true };
      } catch (error) {
        debug.error("TalentAnalytics", "Error logging talent booking inquiry", error);
        return handleFirebaseError(error, "Error logging talent booking inquiry");
      }
    },

    logBookingConversion: async (talentUID, bookingDetails) => {
      try {
        debug.log("TalentAnalytics", "Logging booking conversion", { talentUID, ...bookingDetails });
        
        // Ensure document exists before updating
        await AnalyticsService.ensureAnalyticsDocumentExists(talentUID, 'Talent');
        
        const ref = doc(db, "TalentAnalytics", talentUID);
        await updateDoc(ref, {
          "bookingMetrics.totalBookedGigs": increment(1),
          "bookingMetrics.monthlyBookings": arrayUnion({
            date: serverTimestamp(),
            ...bookingDetails,
          }),
        });
        
        // Log analytics event
        await logCustomEvent('talent_booking_conversion', {
          talent_uid: talentUID,
          ...bookingDetails
        });
        
        return { success: true };
      } catch (error) {
        debug.error("TalentAnalytics", "Error logging talent booking conversion", error);
        return handleFirebaseError(error, "Error logging talent booking conversion");
      }
    },
    
    // Track booking metrics for talent - integrated from bookingAnalyticsService
    trackBookingAction: async (talentUID, action, bookingData = {}) => {
      try {
        debug.log("TalentAnalytics", `Tracking booking action: ${action}`, { talentUID, ...bookingData });
        
        // Ensure document exists before updating
        await AnalyticsService.ensureAnalyticsDocumentExists(talentUID, 'Talent');
        
        const ref = doc(db, "TalentAnalytics", talentUID);
        
        // Standard metrics 
        const updates = {
          "bookingMetrics.totalRequests": increment(1),
          "updatedAt": serverTimestamp()
        };
        
        // Action-specific metrics
        if (action === 'accept' || action === 'confirm') {
          updates["bookingMetrics.acceptedRequests"] = increment(1);
        } else if (action === 'decline' || action === 'reject') {
          updates["bookingMetrics.declinedRequests"] = increment(1);
        } else if (action === 'complete') {
          updates["bookingMetrics.totalCompletedGigs"] = increment(1);
        }
        
        // Calculate acceptance rate
        // Get current metrics
        const docSnapshot = await getDoc(ref);
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const acceptedRequests = (data.bookingMetrics?.acceptedRequests || 0) + 
                                  (action === 'accept' || action === 'confirm' ? 1 : 0);
          const totalRequests = (data.bookingMetrics?.totalRequests || 0) + 1;
          
          if (totalRequests > 0) {
            updates["bookingMetrics.acceptanceRate"] = acceptedRequests / totalRequests;
          }
        }
        
        // Update the document
        debug.firebase("TalentAnalytics", "Updating booking metrics", { talentUID, updates });
        await updateDoc(ref, updates);
        
        // Log analytics event 
        await logCustomEvent('talent_booking_action', {
          talent_uid: talentUID,
          action,
          ...bookingData
        });
        
        return { success: true, action };
      } catch (error) {
        debug.error("TalentAnalytics", `Error tracking talent booking action: ${action}`, error);
        return handleFirebaseError(error, `Error tracking talent booking action: ${action}`);
      }
    },
    
    // Get booking statistics for a talent
    getBookingStats: async (talentUID) => {
      try {
        debug.log("TalentAnalytics", "Getting booking stats", { talentUID });
        const ref = doc(db, "TalentAnalytics", talentUID);
        const snapshot = await getDoc(ref);
        
        if (snapshot.exists()) {
          const data = snapshot.data();
          const bookingMetrics = data.bookingMetrics || {};
          
          return {
            pending: (bookingMetrics.totalRequests || 0) - 
                    (bookingMetrics.acceptedRequests || 0) - 
                    (bookingMetrics.declinedRequests || 0),
            accepted: bookingMetrics.acceptedRequests || 0,
            declined: bookingMetrics.declinedRequests || 0,
            completed: bookingMetrics.totalCompletedGigs || 0,
            acceptanceRate: bookingMetrics.acceptanceRate || 0,
            repeatClients: bookingMetrics.repeatsPerOrganiser || 0,
            success: true
          };
        }
        
        return {
          pending: 0,
          accepted: 0,
          declined: 0,
          completed: 0,
          acceptanceRate: 0,
          repeatClients: 0,
          success: true
        };
      } catch (error) {
        debug.error("TalentAnalytics", "Error getting talent booking stats", error);
        return handleFirebaseError(error, "Error getting talent booking stats");
      }
    },
    
    detectRepeatClient: async (talentUID, organiserUID) => {
      try {
        debug.log("TalentAnalytics", "Detecting repeat client", { talentUID, organiserUID });
        const bookingsRef = collection(db, 'Bookings');
        const clientBookingsQuery = query(
          bookingsRef, 
          where('talentUID', '==', talentUID),
          where('organiserUID', '==', organiserUID)
        );
        
        const snapshot = await getDocs(clientBookingsQuery);
        
        if (snapshot.size > 1) {
          // This is a repeat client - update analytics
          debug.log("TalentAnalytics", "Repeat client detected", { bookingCount: snapshot.size });
          // Ensure document exists before updating
          await AnalyticsService.ensureAnalyticsDocumentExists(talentUID, 'Talent');
          
          const ref = doc(db, "TalentAnalytics", talentUID);
          await updateDoc(ref, {
            "repeatClients": arrayUnion(organiserUID),
            "bookingMetrics.repeatsPerOrganiser": increment(1)
          });
          
          await logCustomEvent('talent_repeat_client', {
            talent_uid: talentUID,
            organiser_uid: organiserUID,
            booking_count: snapshot.size
          });
          
          return { 
            success: true, 
            isRepeatClient: true, 
            bookingCount: snapshot.size 
          };
        }
        
        return { 
          success: true, 
          isRepeatClient: false, 
          bookingCount: snapshot.size 
        };
      } catch (error) {
        debug.error("TalentAnalytics", "Error detecting repeat client", error);
        return handleFirebaseError(error, "Error detecting repeat client for talent");
      }
    }
  },

  // Organiser analytics
  organiser: {
    logTalentBooking: async (organiserUID, bookingDetails) => {
      try {
        debug.log("OrganiserAnalytics", "Logging talent booking", { organiserUID, ...bookingDetails });
        const ref = doc(db, "OrganiserAnalytics", organiserUID);
        await updateDoc(ref, {
          "bookingMetrics.totalBookings": increment(1),
          "bookingMetrics.bookingsThisMonth": increment(1),
          "bookingMetrics.bookingsThisYear": increment(1),
          "bookingMetrics.averageBookingAmount": increment(bookingDetails.amount || 0),
          "talentEngagementMetrics.totalTalentsEngaged": increment(1),
          ...(bookingDetails.isRepeatTalent ? {
            "talentEngagementMetrics.repeatTalentBookings.totalRepeatBookings": increment(1)
          } : {})
        });
        
        // Log analytics event
        await logCustomEvent('organiser_talent_booking', {
          organiser_uid: organiserUID,
          ...bookingDetails
        });
        
        return { success: true };
      } catch (error) {
        debug.error("OrganiserAnalytics", "Error logging organiser talent booking", error);
        return handleFirebaseError(error, "Error logging organiser talent booking");
      }
    },

    // Track booking actions for organisers
    trackBookingAction: async (organiserUID, action, bookingData = {}) => {
      try {
        debug.log("OrganiserAnalytics", `Tracking booking action: ${action}`, { organiserUID, ...bookingData });
        const ref = doc(db, "OrganiserAnalytics", organiserUID);
        
        // Standard metrics 
        const updates = {
          "updatedAt": serverTimestamp()
        };
        
        // Action-specific metrics
        if (action === 'request') {
          updates["bookingMetrics.totalBookings"] = increment(1);
          updates["bookingMetrics.bookingsThisMonth"] = increment(1);
          updates["bookingMetrics.bookingsThisYear"] = increment(1);
        } else if (action === 'cancel') {
          updates["bookingMetrics.cancellations"] = increment(1);
        } else if (action === 'complete') {
          updates["bookingMetrics.completedBookings"] = increment(1);
          
          // Update financial metrics if payment amount is provided
          if (bookingData.amount) {
            updates["paymentMetrics.totalPaymentsMade"] = increment(bookingData.amount);
            updates["paymentMetrics.completedPayments"] = increment(1);
            
            // Update average payment - get current values first
            const docSnapshot = await getDoc(ref);
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              const currentTotal = data.paymentMetrics?.totalPaymentsMade || 0;
              const currentCount = data.paymentMetrics?.completedPayments || 0;
              
              // Calc new average (current average approach)
              if (currentCount > 0) {
                updates["paymentMetrics.averagePaymentAmount"] = 
                  (currentTotal + bookingData.amount) / (currentCount + 1);
              }
            }
          }
        }
        
        // Update the document
        debug.firebase("OrganiserAnalytics", "Updating booking metrics", { organiserUID, updates });
        await updateDoc(ref, updates);
        
        // Log analytics event 
        await logCustomEvent('organiser_booking_action', {
          organiser_uid: organiserUID,
          action,
          ...bookingData
        });
        
        return { success: true, action };
      } catch (error) {
        debug.error("OrganiserAnalytics", `Error tracking organiser booking action: ${action}`, error);
        return handleFirebaseError(error, `Error tracking organiser booking action: ${action}`);
      }
    },
    
    // Get booking statistics for an organiser
    getBookingStats: async (organiserUID) => {
      try {
        debug.log("OrganiserAnalytics", "Getting booking stats", { organiserUID });
        const ref = doc(db, "OrganiserAnalytics", organiserUID);
        const snapshot = await getDoc(ref);
        
        if (snapshot.exists()) {
          const data = snapshot.data();
          const bookingMetrics = data.bookingMetrics || {};
          const paymentMetrics = data.paymentMetrics || {};
          const talentEngagementMetrics = data.talentEngagementMetrics || {};
          
          return {
            totalBookings: bookingMetrics.totalBookings || 0,
            bookingsThisMonth: bookingMetrics.bookingsThisMonth || 0,
            bookingsThisYear: bookingMetrics.bookingsThisYear || 0,
            cancellations: bookingMetrics.cancellations || 0,
            averageBookingAmount: bookingMetrics.averageBookingAmount || 0,
            totalTalentsEngaged: talentEngagementMetrics.totalTalentsEngaged || 0,
            repeatTalentBookings: talentEngagementMetrics.repeatTalentBookings?.totalRepeatBookings || 0,
            totalPaymentsMade: paymentMetrics.totalPaymentsMade || 0,
            success: true
          };
        }
        
        return {
          totalBookings: 0,
          bookingsThisMonth: 0,
          bookingsThisYear: 0,
          cancellations: 0,
          averageBookingAmount: 0,
          totalTalentsEngaged: 0,
          repeatTalentBookings: 0,
          totalPaymentsMade: 0,
          success: true
        };
      } catch (error) {
        debug.error("OrganiserAnalytics", "Error getting organiser booking stats", error);
        return handleFirebaseError(error, "Error getting organiser booking stats");
      }
    },
    
    detectRepeatTalent: async (organiserUID, talentUID) => {
      try {
        debug.log("OrganiserAnalytics", "Detecting repeat talent", { organiserUID, talentUID });
        const bookingsRef = collection(db, 'Bookings');
        const talentBookingsQuery = query(
          bookingsRef, 
          where('organiserUID', '==', organiserUID),
          where('talentUID', '==', talentUID)
        );
        
        const snapshot = await getDocs(talentBookingsQuery);
        
        if (snapshot.size > 1) {
          // This is a repeat talent booking - update analytics
          debug.log("OrganiserAnalytics", "Repeat talent detected", { bookingCount: snapshot.size });
          const ref = doc(db, "OrganiserAnalytics", organiserUID);
          
          const updates = {
            "talentEngagementMetrics.repeatTalentBookings.totalRepeatBookings": increment(1)
          };
          
          // Determine if this is the first repeat for this talent
          const docSnapshot = await getDoc(ref);
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const repeatTalents = data.talentEngagementMetrics?.repeatTalentBookings?.talents || [];
            
            if (!repeatTalents.includes(talentUID)) {
              updates["talentEngagementMetrics.repeatTalentBookings.talents"] = arrayUnion(talentUID);
            }
          }
          
          await updateDoc(ref, updates);
          
          await logCustomEvent('organiser_repeat_talent', {
            organiser_uid: organiserUID,
            talent_uid: talentUID,
            booking_count: snapshot.size
          });
          
          return { 
            success: true, 
            isRepeatTalent: true, 
            bookingCount: snapshot.size 
          };
        }
        
        return { 
          success: true, 
          isRepeatTalent: false, 
          bookingCount: snapshot.size 
        };
      } catch (error) {
        debug.error("OrganiserAnalytics", "Error detecting repeat talent", error);
        return handleFirebaseError(error, "Error detecting repeat talent for organiser");
      }
    }
  },

  // Business events logging
  logBusinessEvent: async (eventType, eventData) => {
    try {
      debug.log("BusinessAnalytics", `Logging business event: ${eventType}`, eventData);
      await logCustomEvent(`business_event_${eventType}`, eventData);
      return { success: true, eventType };
    } catch (error) {
      debug.error("BusinessAnalytics", `Error logging business event: ${eventType}`, error);
      return handleFirebaseError(error, `Error logging business event: ${eventType}`);
    }
  },

  // Performance tracking
  performanceTracking: {
    logPageLoad: async (pageName, loadTime) => {
      try {
        debug.log("PerformanceTracking", `Logging page load: ${pageName}`, { loadTime });
        await logCustomEvent('page_performance', {
          page_name: pageName,
          load_time: loadTime,
          timestamp: new Date().toISOString()
        });
        return { success: true, page: pageName };
      } catch (error) {
        debug.error("PerformanceTracking", `Error logging page load performance: ${pageName}`, error);
        return handleFirebaseError(error, `Error logging page load performance: ${pageName}`);
      }
    },

    logAPICall: async (apiEndpoint, responseTime, status) => {
      try {
        debug.log("PerformanceTracking", `Logging API call: ${apiEndpoint}`, { responseTime, status });
        await logCustomEvent('api_performance', {
          endpoint: apiEndpoint,
          response_time: responseTime,
          status_code: status,
          timestamp: new Date().toISOString()
        });
        return { success: true, endpoint: apiEndpoint };
      } catch (error) {
        debug.error("PerformanceTracking", `Error logging API performance: ${apiEndpoint}`, error);
        return handleFirebaseError(error, `Error logging API performance: ${apiEndpoint}`);
      }
    }
  },

  // User engagement tracking
  userEngagement: {
    logFeatureUsage: async (userUID, featureName) => {
      try {
        debug.log("UserEngagement", `Logging feature usage: ${featureName}`, { userUID });
        await logCustomEvent('feature_usage', {
          user_uid: userUID,
          feature: featureName,
          timestamp: new Date().toISOString()
        });
        return { success: true, feature: featureName };
      } catch (error) {
        debug.error("UserEngagement", `Error logging feature usage: ${featureName}`, error);
        return handleFirebaseError(error, `Error logging feature usage: ${featureName}`);
      }
    },

    logSessionDuration: async (userUID, sessionDuration) => {
      try {
        debug.log("UserEngagement", "Logging session duration", { userUID, sessionDuration });
        await logCustomEvent('session_duration', {
          user_uid: userUID,
          duration_seconds: sessionDuration,
          timestamp: new Date().toISOString()
        });
        return { success: true };
      } catch (error) {
        debug.error("UserEngagement", "Error logging session duration", error);
        return handleFirebaseError(error, "Error logging session duration");
      }
    }
  },
  // Error tracking
  errorTracking: {
    logError: async (errorType, errorDetails) => {
      try {
        debug.log("ErrorTracking", `Logging error: ${errorType}`, errorDetails);
        await logCustomEvent('application_error', {
          error_type: errorType,
          error_details: JSON.stringify(errorDetails),
          timestamp: new Date().toISOString()
        });
        return { success: true, errorType };
      } catch (error) {
        // Intentionally do NOT console.error here: this runs from the global
        // console.error override, so re-logging would re-enter it and create an
        // endless permission-denied flood. Swallow quietly (writes to analytics
        // are server-only and expected to fail from the client).
        return { success: false, error: "Logging infrastructure error" };
      }
    }
  },
  
  // Admin analytics methods
  admin: {
    // Get platform-wide analytics
    getPlatformAnalytics: async () => {
      try {
        debug.log("AdminAnalytics", "Getting platform-wide analytics");
        // Fetch talent analytics
        const talentAnalyticsRef = collection(db, "TalentAnalytics");
        const talentSnapshot = await getDocs(talentAnalyticsRef);
        
        // Fetch organiser analytics
        const organiserAnalyticsRef = collection(db, "OrganiserAnalytics");
        const organiserSnapshot = await getDocs(organiserAnalyticsRef);
        
        // Process the data
        const talentData = talentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const organiserData = organiserSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Calculate aggregate metrics
        const aggregateMetrics = {
          totalUsers: talentData.length + organiserData.length,
          totalTalents: talentData.length,
          totalOrganisers: organiserData.length,
          totalBookings: talentData.reduce((total, user) => 
            total + (user.bookingMetrics?.totalBookedGigs || 0), 0),
          totalProfileViews: talentData.reduce((total, user) => 
            total + (user.profileViews?.totalViews || 0), 0),
          // Add more aggregate metrics as needed
        };
        
        return {
          success: true,
          aggregateMetrics,
          talentAnalytics: talentData,
          organiserAnalytics: organiserData
        };
      } catch (error) {
        debug.error("AdminAnalytics", "Error fetching platform analytics", error);
        return handleFirebaseError(error, "Error fetching platform analytics");
      }
    },
    
    // Get analytics for a specific date range
    getAnalyticsForDateRange: async (startDate, endDate) => {
      try {
        debug.log("AdminAnalytics", "Getting analytics for date range", { startDate, endDate });
        // Implementation for date filtering
        // This would require timestamp filtering in Firestore queries
        
        return { success: true, data: [] }; // Replace with actual implementation
      } catch (error) {
        debug.error("AdminAnalytics", "Error fetching analytics for date range", error);
        return handleFirebaseError(error, "Error fetching analytics for date range");
      }
    },
    
    // Get user-specific analytics
    getUserAnalytics: async (userUID) => {
      try {
        debug.log("AdminAnalytics", "Getting user analytics", { userUID });
        // Try to get from TalentAnalytics first
        let userDoc = await getDoc(doc(db, "TalentAnalytics", userUID));
        
        // If not found, try OrganiserAnalytics
        if (!userDoc.exists()) {
          userDoc = await getDoc(doc(db, "OrganiserAnalytics", userUID));
        }
        
        if (userDoc.exists()) {
          return {
            success: true,
            analytics: {
              id: userDoc.id,
              ...userDoc.data()
            }
          };
        }
        
        return {
          success: false,
          error: "No analytics found for this user"
        };
      } catch (error) {
        debug.error("AdminAnalytics", "Error fetching user analytics", error);
        return handleFirebaseError(error, "Error fetching user analytics");
      }
    }
  }
};

export const detectRepeatClient = async (talentUID, organiserUID) => {
  debug.log("AnalyticsService", "Detecting repeat client", { talentUID, organiserUID });
  return await AnalyticsService.talent.detectRepeatClient(talentUID, organiserUID);
};

export const trackBookingMetrics = async (talentUID, action, bookingData = {}) => {
  debug.log("AnalyticsService", "Tracking booking metrics", { talentUID, action, bookingData });
  return await AnalyticsService.talent.trackBookingAction(talentUID, action, bookingData);
};

// Export all analytics-related functions
export default {
  logCustomEvent,
  setUserAnalyticsProperties,
  trackScreenView,
  logRegistrationProgress,
  logVisit,
  detectRepeatClient,  // Added this line
  trackBookingMetrics, // Added this line
  AnalyticsService
};